import { useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { BreakRequestsTheme } from './types';

type StartMinute = 0 | 15 | 30 | 45;

const HOUR_OPTIONS: readonly number[] = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_OPTIONS: readonly StartMinute[] = [0, 15, 30, 45];
const DURATION_OPTIONS: readonly number[] = [15, 30, 45, 60];

export interface CounterOfferModalProps {
  testerAlias: string;
  /**
   * The current window being countered (the latest proposal). Pre-fills the
   * form so the user lands on the existing slot instead of an empty picker —
   * counter-offers are usually nudges, not wholesale resets.
   */
  currentStartsAt: string;
  currentEndsAt: string;
  /** Wall-clock label of the current window — shown above the form for context. */
  currentWindowLabel: string;
  busy: boolean;
  /**
   * Caller-supplied verb so the modal's primary action matches the surface
   * ("Suggest" vs "Suggest counter") without coupling the modal to viewerRole
   * semantics. The underlying lifecycle status is `suggested` on both sides.
   */
  submitLabel: string;
  /** Posts the counter — caller routes admin vs tester at the API layer. */
  onConfirm: (params: {
    startsAt: string;
    endsAt: string;
    rationale: string | undefined;
  }) => Promise<void>;
  onCancel: () => void;
  theme: BreakRequestsTheme;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function utcDate(iso: string): string {
  return iso.slice(0, 10);
}
function utcHour(iso: string): number {
  return Number(iso.slice(11, 13));
}
function utcMinute(iso: string): StartMinute {
  // Snap any non-quarter-hour minute down to the nearest quarter.
  const raw = Number(iso.slice(14, 16));
  const snapped = Math.floor(raw / 15) * 15;
  return (snapped as StartMinute);
}
function durationMinutes(startsAt: string, endsAt: string): number {
  return Math.round((Date.parse(endsAt) - Date.parse(startsAt)) / 60000);
}

function buildWindow(
  date: string,
  hourUtc: number,
  startMinute: StartMinute,
  duration: number,
): { startsAt: string; endsAt: string } {
  const startMs = Date.parse(`${date}T${pad2(hourUtc)}:${pad2(startMinute)}:00.000Z`);
  const endMs = startMs + duration * 60 * 1000;
  return {
    startsAt: new Date(startMs).toISOString(),
    endsAt: new Date(endMs).toISOString(),
  };
}

/**
 * Counter-offer composer (#3772). Mounted by both the web admin row and the
 * Discord-Activity admin drawer (#4258); the caller wires the appropriate API
 * endpoint via `onConfirm` and passes its surface theme via `theme`. Form
 * mirrors `SuggestBreakForm` (UTC hour/minute picker) so the two FM-facing
 * pickers stay visually consistent, and adds a configurable duration
 * (counter-offers commonly shorten/lengthen the existing window, not just
 * shift it).
 */
export function CounterOfferModal({
  testerAlias,
  currentStartsAt,
  currentEndsAt,
  currentWindowLabel,
  busy,
  submitLabel,
  onConfirm,
  onCancel,
  theme,
}: CounterOfferModalProps) {
  const [date, setDate] = useState(utcDate(currentStartsAt));
  const [hourUtc, setHourUtc] = useState(utcHour(currentStartsAt));
  const [startMinute, setStartMinute] = useState<StartMinute>(utcMinute(currentStartsAt));
  const [duration, setDuration] = useState(durationMinutes(currentStartsAt, currentEndsAt));
  const [rationale, setRationale] = useState('');
  const trimmed = rationale.trim();

  const styles = useMemo(() => buildStyles(theme), [theme]);

  async function handleSubmit() {
    const { startsAt, endsAt } = buildWindow(date, hourUtc, startMinute, duration);
    await onConfirm({
      startsAt,
      endsAt,
      rationale: trimmed.length > 0 ? trimmed : undefined,
    });
  }

  // Portal to <body> so the backdrop's stacking context is the document root,
  // not whatever ancestor (sticky coverage rows, isolated lane cells) the
  // mount point happens to sit inside. Without this the modal can paint
  // behind sticky-positioned grid chrome on the schedule page.
  return createPortal(
    <div style={styles.backdrop} role="dialog" aria-modal="true" onClick={onCancel}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <h4 style={styles.title}>Counter-offer</h4>
        <div style={styles.meta}>
          <div>{testerAlias}</div>
          <div style={styles.muted}>Currently: {currentWindowLabel}</div>
        </div>
        <div style={styles.formRow}>
          <label style={styles.label}>
            Date
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={styles.input}
              disabled={busy}
            />
          </label>
          <label style={styles.label}>
            Hour (UTC)
            <select
              value={hourUtc}
              onChange={(e) => setHourUtc(Number(e.target.value))}
              style={styles.input}
              disabled={busy}
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {pad2(h)}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Start
            <select
              value={startMinute}
              onChange={(e) => setStartMinute(Number(e.target.value) as StartMinute)}
              style={styles.input}
              disabled={busy}
            >
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  :{pad2(m)}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Duration
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              style={styles.input}
              disabled={busy}
            >
              {DURATION_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} min
                </option>
              ))}
            </select>
          </label>
        </div>
        <label style={styles.label}>
          Rationale (optional)
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="e.g. iOS coverage is thin at 14:00 — this slot is safer."
            rows={2}
            maxLength={280}
            style={styles.textarea}
            disabled={busy}
          />
        </label>
        <div style={styles.actionsRow}>
          <button
            type="button"
            style={styles.ghostButton}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            style={styles.primaryButton}
            onClick={handleSubmit}
            disabled={busy}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface ModalStyles {
  readonly backdrop: CSSProperties;
  readonly panel: CSSProperties;
  readonly title: CSSProperties;
  readonly meta: CSSProperties;
  readonly muted: CSSProperties;
  readonly formRow: CSSProperties;
  readonly label: CSSProperties;
  readonly input: CSSProperties;
  readonly textarea: CSSProperties;
  readonly actionsRow: CSSProperties;
  readonly ghostButton: CSSProperties;
  readonly primaryButton: CSSProperties;
}

function buildStyles(theme: BreakRequestsTheme): ModalStyles {
  const input: CSSProperties = {
    background: theme.colors.bgTertiary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.xs,
    color: theme.colors.textPrimary,
    fontSize: theme.fontSize.sm,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    outline: 'none',
  };

  const baseButton: CSSProperties = {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.xs,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    fontSize: theme.fontSize.sm,
    fontWeight: 500,
    cursor: 'pointer',
    transition: `all ${theme.transitions.fast}`,
  };

  return {
    backdrop: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    panel: {
      background: theme.colors.bgSecondary,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.radius.sm,
      padding: theme.spacing.md,
      width: 'min(520px, 92vw)',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    title: {
      margin: 0,
      fontSize: theme.fontSize.md,
      color: theme.colors.textPrimary,
      fontWeight: 600,
    },
    meta: {
      fontSize: theme.fontSize.sm,
      color: theme.colors.textPrimary,
    },
    muted: {
      fontSize: theme.fontSize.xs,
      color: theme.colors.textMuted,
    },
    formRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      flexWrap: 'wrap',
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      fontSize: theme.fontSize.xs,
      color: theme.colors.textMuted,
    },
    input,
    textarea: {
      ...input,
      resize: 'vertical',
      minHeight: 56,
      fontFamily: 'inherit',
    },
    actionsRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing.xs,
    },
    ghostButton: {
      ...baseButton,
      background: theme.colors.bgTertiary,
      color: theme.colors.textSecondary,
    },
    primaryButton: {
      ...baseButton,
      background: theme.colors.primary,
      color: theme.colors.primaryButtonText,
      borderColor: theme.colors.primary,
    },
  };
}
